import React, { useState } from "react";
import { Modal, Input, message } from "antd";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import type { StripeCardElementOptions } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import { stripePromise } from "../../../services/stripe";
import { createSetupIntent, setDefaultCard } from "../subscription.service";
import { PaymentMethod } from "../subscription.types";

interface PaymentMethodModalProps {
  open: boolean;
  method: PaymentMethod | null;
  onClose: () => void;
  /** Fired after the card is saved & made default — parent should refetch. */
  onSaved: () => void;
}

const CARD_OPTIONS: StripeCardElementOptions = {
  style: {
    base: {
      fontSize: "15px",
      fontFamily: "Poppins, sans-serif",
      color: "#1f2937",
      "::placeholder": { color: "#9ca3af" },
    },
    invalid: { color: "#dc2626" },
  },
};

const PaymentForm: React.FC<
  Pick<PaymentMethodModalProps, "method" | "onClose" | "onSaved"> & {
    saving: boolean;
    setSaving: (v: boolean) => void;
  }
> = ({ method, onClose, onSaved, saving, setSaving }) => {
  const { t } = useTranslation("subscription");
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState(method?.cardholderName ?? "");

  const handleSave = async () => {
    if (!stripe || !elements) return;
    const card = elements.getElement(CardElement);
    if (!card) return;
    setSaving(true);
    try {
      // 1) SetupIntent from our backend → 2) confirm the card with Stripe.js
      const { clientSecret } = await createSetupIntent();
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card, billing_details: { name } },
      });
      if (result.error) {
        message.error(result.error.message || t("paymentMethod.errors.cardNotSaved"));
        return;
      }
      if (result.setupIntent?.status !== "succeeded") {
        message.error(t("paymentMethod.errors.cardNotSavedRetry"));
        return;
      }
      // 3) make it the default card
      const pmId = result.setupIntent.payment_method;
      if (typeof pmId !== "string") {
        message.error(t("paymentMethod.errors.cardNotSavedRetry"));
        return;
      }
      await setDefaultCard(pmId);
      message.success(t("paymentMethod.success"));
      onSaved();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || t("paymentMethod.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h3 className="sub-modal-title">{t("paymentMethod.title")}</h3>
      <p className="sub-modal-sub">{t("paymentMethod.subtitle")}</p>

      <div className="sub-form-field">
        <label>{t("paymentMethod.cardholderNameLabel")}</label>
        <Input
          placeholder={t("paymentMethod.cardholderNamePlaceholder")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="sub-form-field">
        <label>{t("paymentMethod.cardDetailsLabel")}</label>
        <div className="sub-card-element">
          <CardElement options={CARD_OPTIONS} />
        </div>
      </div>

      <div className="sub-modal-actions">
        <button
          className="sub-btn sub-btn-outline"
          disabled={saving}
          onClick={onClose}
        >
          {t("common.cancel")}
        </button>
        <button
          className="sub-btn sub-btn-primary"
          disabled={saving || !stripe}
          onClick={handleSave}
        >
          {saving ? t("paymentMethod.saving") : t("paymentMethod.save")}
        </button>
      </div>
    </>
  );
};

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  open,
  method,
  onClose,
  onSaved,
}) => {
  const [saving, setSaving] = useState(false);
  return (
    <Modal
      open={open}
      onCancel={() => {
        if (!saving) onClose();
      }}
      maskClosable={!saving}
      keyboard={!saving}
      closable={!saving}
      footer={null}
      centered
      width={460}
      className="sub-modal"
      destroyOnClose
    >
      <Elements stripe={stripePromise}>
        <PaymentForm
          method={method}
          onClose={onClose}
          onSaved={onSaved}
          saving={saving}
          setSaving={setSaving}
        />
      </Elements>
    </Modal>
  );
};

export default PaymentMethodModal;
